import React, { FC, useContext, useMemo } from 'react';
import { Provider } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { Action, Middleware, StoreEnhancer } from 'redux';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import SchemaContext from 'ia-react-core/~/components/schema/_SchemaContext';
import { DeepPartial } from 'ia-react-core/~/interfaces/DeepPartial';
import configureStore from './configureStore';

import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import ScopeContext from '../../../components/redux/_ScopeContext';

export interface WithStoreOptions<State> {
  initialState?: DeepPartial<State> | State;
  reducers?: {
    [key in keyof State]: (state: State[key], action: any) => State[key];
  };
  extendReducer?: (reducer: (state: State, action: Action) => State) => (state: State) => State;
  middlewares?: Middleware<any, State, any>[];
  enhancers?: StoreEnhancer[];
}

export const middlewares: Middleware<any, any, any>[] = [];
export const middlewareFactories: (() => Middleware<any, any, any>)[] = [];
export const storeDecorators: ((store: AugmentedStore<any>) => void)[] = [];

export interface WithStoreMock {}

/**
 * @since 0.2.0
 * @see See [Redux](https://redux.js.org/)
 * @see See [App configuration](/Devguide/App%20configuration)
 * @component
 */
export default function withStore<State extends object>({ initialState, reducers, extendReducer, enhancers = [], middlewares: otherMiddlewares = [] }: WithStoreOptions<State> = {}) {
  return (WrappedComponent: FC<{}>) => {
    const WithStore = (props: {}) => {
      const { schema } = useContext(SchemaContext);

      const storeOptions = useMemo(() => ({
        initialState,
        middlewares: [...middlewares, ...middlewareFactories.map((middlewareFactory) => middlewareFactory()), ...otherMiddlewares],
        reducers,
        storeDecorators,
        extendReducer,
        enhancers,
        schema,
      }), [schema]);

      const store = useMemo(() => configureStore<State>(storeOptions), [storeOptions]);

      const scopeContext = useMemo(() => ({ scope: [], store }), [store]);

      if (process.env.NODE_ENV === 'test') {
        window.store = store;
      }

      return (
        <Provider store={store}>
          <ScopeContext.Provider value={scopeContext}>
            <WrappedComponent {...props} />
          </ScopeContext.Provider>
        </Provider>
      );
    };

    WithStore.displayName = getWrappedDisplayName('WithStore', WrappedComponent);

    return hoistNonReactStatics(WithStore, WrappedComponent);
  };
}
